// @ts-nocheck
import React, { useCallback } from 'react';
import { Box, Stack, Breadcrumbs, Typography, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { map, startCase, isEmpty } from 'lodash';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { ReactComponent as ArtifactIcon } from '../../assets/Icons/ArtifactsIcon.svg';
import { getLabelPathFromLabelConfig, isValueEmpty } from '../../utils/Utils';
import { ReactComponent as ModelIcon } from '../../assets/images/ModelDetails/ModelLogo.svg';
import { ReactComponent as GitPullRequest } from '../../assets/images/ModelDetails/GitPullRequest.svg';
import { ReactComponent as QueryIcon } from '../../assets/Icons/Query 3.svg';
import REGEX from '../../const/Regex';
import useCommon from '../../hooks/useCommon';
import { DEFAULT_ROUTE_CONFIG } from '../../const/defaultRoutesConfig';
import { NOOP } from '../../const/CommonConst';

const LinkLabelComponent = ({
  setNavigateToRoute,
  handleModalOpen,
  to,
  value,
}) => {
  const handelNavigate = useCallback(() => {
    setNavigateToRoute(to);
    handleModalOpen();
  }, [handleModalOpen, setNavigateToRoute, to]);
  return (
    <Typography
      variant="subtitle2"
      sx={{
        cursor: 'pointer',
      }}
      onClick={handelNavigate}
    >
      {value}
    </Typography>
  );
};
LinkLabelComponent.propTypes = {
  setNavigateToRoute: PropTypes.func.isRequired,
  handleModalOpen: PropTypes.func.isRequired,
  to: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const Breadcrumb = ({
  state: breadCrumbData,
  tabName = '',
  handleBreadcrumbClick = NOOP,
  isDashboard,
}) => {
  const { common } = useCommon();
  const theme = useTheme();

  const location = useLocation();
  const paths = location.pathname.split('/').slice(1);
  const allPathsNames = map(paths, (item) => {
    if (REGEX.VALID_ENTITY_ID.exec(item)) {
      return item;
    }
    if (item === 'ai-assets') {
      return getLabelPathFromLabelConfig(
        'MENUS.modelInventory',
        'label',
        startCase(item)
      );
    }
    if (item === 'add-ai-asset') {
      return getLabelPathFromLabelConfig(
        'MENUS.modelInventory.subPaths.createModel',
        'label',
        `Add ${getLabelPathFromLabelConfig(
          'MENUS.modelInventory',
          'featureLabel',
          'AI Asset'
        )}`
      );
    }
    return startCase(item);
  });

  const pathNames = allPathsNames.map((item) => {
    if (item === 'ModelAssociation') {
      return `Model ${getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelAssociation`,
        'featureLabel',
        'Artifact'
      )}`;
    }
    if (item === 'ModelInventory') {
      return `My ${getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelInventory`,
        'featureLabel',
        'Model'
      )}s`;
    }
    if (item === 'Workflow') {
      return 'Workflow';
    }
    if (item === 'EditWorkflow') {
      return 'Edit Workflow';
    }
    if (item === 'Query') {
      return 'Query Inventory';
    }
    return item;
  });
  const [searchParams] = useSearchParams();
  const templateName = searchParams.get('eid');
  const workFlowName = searchParams.get('workflowType');

  const homePath = getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.home?.keyPath || ``,
    'path',
    DEFAULT_ROUTE_CONFIG?.home?.path
  );

  const homeLabel = getLabelPathFromLabelConfig(
    DEFAULT_ROUTE_CONFIG?.home?.keyPath || ``,
    'label',
    DEFAULT_ROUTE_CONFIG?.home?.path
  );

  // This is to workflows compile warning feature
  const renderLinkTag = (to, value) => {
    if (to !== '/workflows/edit-workflow') {
      if (common?.isWorkflowCompiled) {
        return (
          <Link
            className="custom-link"
            tabIndex={0} // Make it focusable
            style={{ textDecoration: 'inherit' }}
            to={to}
            aria-label={`${value}`}
          >
            <Typography variant="subtitle1">{value}</Typography>
          </Link>
        );
      }
    }
    return (
      <Link style={{ textDecoration: 'inherit' }} to={to}>
        <Typography variant="subtitle2">{value}</Typography>
      </Link>
    );
  };

  const handleKeyDown = useCallback((event) => {
    // Handle "Enter" or "Space" key to activate the breadcrumb
    if (event.key === 'Enter' || event.key === ' ') {
      window.location.href = `/${homePath}?value=0`;
    }
  }, []);

  const renderIcon = (path) => {
    if (path?.type === 'ModelInventory') {
      const modelInventoryLabel = getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelInventory`,
        'featureLabel',
        'Model'
      );
      return (
        <Box
          title={modelInventoryLabel}
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ModelIcon
            style={{
              width: '18px',
              height: '18px',
              color: theme?.palette?.entityColor?.model,
            }}
          />
        </Box>
      );
    }

    if (path?.type === 'ModelRequest') {
      const modelRequestLabel = getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelRequest`,
        'featureLabel',
        'Assessment'
      );
      return (
        <Box
          title={modelRequestLabel}
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <GitPullRequest
            style={{
              width: '18px',
              height: '18px',
              color: theme?.palette?.entityColor?.assessment,
            }}
          />
        </Box>
      );
    }

    // The type when normal tab switch is done it appears to be useCase while in the case of issue type tab switches it appears to be UseCase
    if (path?.type === 'UseCase' || path?.type === 'useCase') {
      const useCaseLabel = getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.UseCase`,
        'featureLabel',
        'UseCase'
      );
      return (
        <Box
          title={useCaseLabel}
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Diversity2Icon
            style={{
              width: '18px',
              height: '18px',
              color: theme?.palette?.entityColor?.useCase,
            }}
          />
        </Box>
      );
    }
    if (path?.type === 'accessRequest' || path?.type === 'AccessRequest') {
      const accessRequestLabel = getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.AccessRequest`,
        'featureLabel',
        'AccessRequest'
      );
      return (
        <Box
          title={accessRequestLabel}
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <VpnKeyIcon
            style={{
              width: '18px',
              height: '18px',
              color: theme?.palette?.entityColor?.accessRequest,
            }}
          />
        </Box>
      );
    }

    const queryLabel = getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.Query`,
      'featureLabel',
      'Query'
    );
    if (path?.type?.toLowerCase() === queryLabel.toLowerCase()) {
      return (
        <Box
          title={queryLabel}
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <QueryIcon
            style={{
              width: '18px',
              height: '18px',
              color: theme?.palette?.entityColor?.query,
            }}
          />
        </Box>
      );
    }

    const modelAssociationLabel = getLabelPathFromLabelConfig(
      `TEMPLATE_TYPES.ModelAssociation`,
      'featureLabel',
      'Artifact'
    );

    return (
      <Box title={modelAssociationLabel} component="span">
        <ArtifactIcon
          style={{
            width: '20px',
            height: '20px',
          }}
        />
      </Box>
    );
  };

  const getEntityTypeLabel = (path) => {
    if (path?.label === 'ModelInventory') {
      return `My ${getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelInventory`,
        'featureLabel',
        'Model'
      )}`;
    }
    if (path?.label === 'ModelAssociation') {
      return `Model ${getLabelPathFromLabelConfig(
        `TEMPLATE_TYPES.ModelAssociation`,
        'featureLabel',
        'Artifact'
      )}`;
    }
    if (path?.label === 'useCase') {
      return 'Use Case';
    }
    return path?.label;
  };

  const handleDashboardKeydown = useCallback(
    (event) => {
      if (event?.key === 'Enter' || event?.key === ' ') {
        handleBreadcrumbClick();
      }
    },
    [handleBreadcrumbClick]
  );

  const renderBreadcrumbLinkFromData = (path, i) => {
    return (
      <Box key={path?.path} sx={{ display: 'flex', justifyContent: 'center' }}>
        {!isEmpty(path?.type) ? renderIcon(path) : null}
        <Link
          style={{ textDecoration: 'inherit', cursor: 'pointer' }}
          to={path?.path}
          aria-label={getEntityTypeLabel(path)}
          className="custom-link"
          tabIndex={0}
          state={breadCrumbData.slice(0, i + 1)}
        >
          <Typography variant="subtitle1">
            {getEntityTypeLabel(path)}
          </Typography>
        </Link>
      </Box>
    );
  };

  const renderDashboardLink = (value) => {
    return (
      <Link
        className="custom-link"
        style={{
          textDecoration: 'none',
          cursor: 'pointer',
        }}
        onKeyDown={handleKeyDown}
        aria-label={homeLabel}
        tabIndex={0}
        color="inherit"
        to={`/${homePath}`}
      >
        <Typography
          variant="subtitle1"
          aria-label={value}
          onKeyDown={handleDashboardKeydown}
          onClick={handleBreadcrumbClick}
        >
          {value}
        </Typography>
      </Link>
    );
  };

  const renderTitleOfPage = (pageTitle = 'error') => {
    return (
      <Box>
        <Typography
          component="h1"
          variant="h3"
          aria-label={pageTitle}
          tabIndex={0}
          aria-current="page"
        >
          {pageTitle}
        </Typography>
      </Box>
    );
  };

  const renderLastBreadcrumbFromPath = (value, to) => {
    const boxStyles = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    };

    if (
      isValueEmpty(tabName) ||
      isValueEmpty(templateName) ||
      isValueEmpty(workFlowName)
    ) {
      boxStyles.height = '100%';
    }

    return (
      <Box sx={boxStyles}>
        {!isValueEmpty(breadCrumbData) ? renderIcon(breadCrumbData) : null}

        {isDashboard ? (
          renderDashboardLink(value)
        ) : (
          <Typography
            key={to}
            variant="subtitle2"
            component="h1"
            aria-label={breadCrumbData?.uniqueId || value}
            tabIndex={0}
            aria-current="page"
          >
            {breadCrumbData?.uniqueId || value}
          </Typography>
        )}
      </Box>
    );
  };

  const renderLastBreadcrumbFromData = (path) => {
    return (
      <Box
        component="h1"
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        tabIndex={0}
        aria-label={path?.label}
      >
        {!isEmpty(path?.type) ? renderIcon(path) : null}
        <Typography
          key={path?.path}
          variant="subtitle2"
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          {path?.label}
        </Typography>
      </Box>
    );
  };

  const renderBreadcrumbs = () => {
    if (breadCrumbData === 1 || pathNames?.length === 1) {
      const pageTitle = pathNames[0] || breadCrumbData[0]?.path?.label;
      return renderTitleOfPage(pageTitle);
    }
    if (breadCrumbData?.length > 0) {
      return breadCrumbData.map((path, i) => {
        const isLastItem = i === Number(breadCrumbData?.length) - 1;

        if (isLastItem) {
          return renderLastBreadcrumbFromData(path);
        }
        return renderBreadcrumbLinkFromData(path, i);
      });
    }
    return pathNames.map((value, index) => {
      const to = `/${paths.slice(0, index + 1).join('/')}`;
      const isLastItem = index === paths.length - 1;

      if (isLastItem) {
        return renderLastBreadcrumbFromPath(value, to);
      }
      return <Box key={to}>{renderLinkTag(to, value)}</Box>;
    });
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Stack
        spacing={2}
        height="100%"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Breadcrumbs
          separator={
            <ArrowForwardIosIcon
              sx={{
                height: '16px',
                color: (_theme) => _theme?.palette?.text?.disabled,
              }}
            />
          }
          aria-label="breadcrumb"
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& .MuiBreadcrumbs-ol': {
              height: '100%',
            },
            '& .MuiBreadcrumbs-li': {
              '& span': {
                paddingRight: '8px',
              },

              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
          }}
        >
          {renderBreadcrumbs()}

          {/* When we are in the workflow route we want to show the workflow name in edit mode */}
          {workFlowName ? (
            <Typography
              variant="subtitle2"
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'pre-wrap',
              }}
              aria-label={workFlowName}
              tabIndex={0}
              aria-current="page"
            >
              {workFlowName}
            </Typography>
          ) : null}
          {/* If we are in edit template then we want to show the template name */}
          {templateName ? (
            <Typography
              variant="subtitle2"
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              aria-label={templateName}
              tabIndex={0}
              aria-current="page"
            >
              {templateName}
            </Typography>
          ) : null}
        </Breadcrumbs>
      </Stack>
    </Box>
  );
};
export default Breadcrumb;
Breadcrumb.propTypes = {
  state: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  tabName: PropTypes.string.isRequired,
  handleBreadcrumbClick: PropTypes.func.isRequired,
  isDashboard: PropTypes.bool.isRequired,
};
